/* Verqo — Investor / Partner Deck.
   Two-page scrollable deck on a public `/pitch` route. Matches the
   verqo dark-charcoal + lime-accent brand from the buyer login page.
*/
import { useEffect, useState } from "react";
import {
  Banknote, Zap, ShieldCheck, Globe, TrendingUp, Layers,
  Sparkles, ArrowRight, CheckCircle2, AlertCircle, Building2, Network,
  CircleDot, Code2, Plug, AppWindow, Truck, CreditCard, Boxes, Download,
} from "lucide-react";
import { VerqoLockup } from "../components/VerqoLockup";

/* ═══ Iter 245 · Pitch Index nav ═══════════════════════════════════════════
   Sticky, minimal top-of-page nav that lets investors jump directly to any
   slide/section. Mirrors the "table of contents" affordance in a physical
   deck. Uses IntersectionObserver to highlight the active section so
   readers keep their bearings while scrolling. */
const PITCH_SECTIONS = [
  { id: "top",        label: "Cover" },
  { id: "problem",    label: "Problem" },
  { id: "product",    label: "Product" },
  { id: "team",       label: "Team" },
  { id: "traction",   label: "Traction" },
  { id: "why-now",    label: "Why now" },
  { id: "market",     label: "Market" },
  { id: "business",   label: "Business" },
  { id: "competition",label: "Compete" },
  { id: "roadmap",    label: "Roadmap" },
  { id: "ask",        label: "The ask" },
  { id: "exit",       label: "Exit" },
];

function PitchIndex() {
  const [active, setActive] = useState("top");
  useEffect(() => {
    const nodes = PITCH_SECTIONS
      .map(s => document.getElementById(s.id))
      .filter(Boolean);
    if (!nodes.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        // Pick the entry closest to the top of the viewport that is visible.
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target?.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    nodes.forEach(n => io.observe(n));
    return () => io.disconnect();
  }, []);

  const jump = (id) => (e) => {
    e.preventDefault();
    if (id === "top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setActive("top");
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#0a0e0a]/85 border-b border-white/[0.06]"
      data-testid="pitch-index-nav"
      aria-label="Pitch deck sections"
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 h-14 flex items-center gap-3">
        {/* Brand lockup + tagline — single header for the whole deck */}
        <a
          href="#top"
          onClick={jump("top")}
          className="flex items-center gap-2.5 flex-shrink-0"
          aria-label="Back to top"
          data-testid="pitch-index-brand"
        >
          <VerqoLockup size="sm" dark testid="pitch-index-logo" />
          <span className="hidden md:inline text-[12px] text-white/50 border-l border-white/10 pl-2.5 tracking-tight whitespace-nowrap" data-testid="pitch-header-tagline">
            · Wholesale, instant.
          </span>
        </a>

        {/* Section pills — center-aligned, horizontally scrollable on mobile */}
        <div className="flex-1 flex items-center gap-1 overflow-x-auto no-scrollbar justify-end lg:justify-center">
          {PITCH_SECTIONS.filter(s => s.id !== "top").map(s => {
            const isActive = active === s.id;
            return (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={jump(s.id)}
                data-testid={`pitch-index-${s.id}`}
                aria-current={isActive ? "true" : undefined}
                className={
                  "px-2.5 py-1 rounded-md text-[11.5px] tracking-tight whitespace-nowrap transition-colors flex-shrink-0 " +
                  (isActive
                    ? "bg-lime-400/15 text-lime-300"
                    : "text-white/55 hover:text-white hover:bg-white/[0.04]")
                }
              >
                {s.label}
              </a>
            );
          })}
        </div>

        {/* Confidential badge + Download PDF — pinned right on wide screens */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={() => {
              // Swap the tab title so Chrome's Save-as-PDF dialog defaults to
              // a nice filename ("Verqo — Pitch Deck (2026).pdf") instead of
              // the marketing tagline; restore after the dialog closes.
              const originalTitle = document.title;
              document.title = "Verqo — Pitch Deck (2026)";
              // Give the browser a tick to apply the `.pitch-printing` class
              // that hides the sticky nav before Chromium captures the page.
              document.documentElement.classList.add("pitch-printing");
              setTimeout(() => {
                window.print();
                // Reset after the user closes the print dialog.
                setTimeout(() => {
                  document.documentElement.classList.remove("pitch-printing");
                  document.title = originalTitle;
                }, 500);
              }, 60);
            }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-medium tracking-tight border border-lime-400/40 bg-lime-400/10 text-lime-300 hover:bg-lime-400/20 hover:border-lime-400/60 transition-colors whitespace-nowrap"
            data-testid="pitch-download-pdf"
            aria-label="Download deck as PDF"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Download PDF</span>
          </button>
          <div className="hidden lg:block text-[9.5px] uppercase tracking-[0.24em] text-white/40 whitespace-nowrap" data-testid="pitch-header-confidential">
            Confidential · 2026
          </div>
        </div>
      </div>
    </nav>
  );
}

function Eyebrow({ children, testid }) {
  return (
    <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.25em] text-lime-300" data-testid={testid}>
      <CircleDot className="w-2.5 h-2.5 fill-lime-300 text-lime-300" />
      {children}
    </div>
  );
}

function Stat({ value, label, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-3xl md:text-4xl font-semibold tracking-tight text-white tabular-nums">
        {value}
      </div>
      <div className="text-[11px] uppercase tracking-widest text-white/40 mt-1.5">{label}</div>
    </div>
  );
}

function Pillar({ icon: Icon, title, body, testid }) {
  return (
    <div className="border-l-2 border-lime-400/40 pl-5 py-2" data-testid={testid}>
      <div className="flex items-center gap-2 text-lime-300">
        <Icon className="w-3.5 h-3.5" />
        <div className="text-[11px] uppercase tracking-[0.18em]">{title}</div>
      </div>
      <div className="mt-2 text-[13px] leading-relaxed text-white/75">{body}</div>
    </div>
  );
}

function Bullet({ children, testid }) {
  return (
    <div className="flex gap-2 items-start text-[13px] leading-relaxed text-white/75" data-testid={testid}>
      <CheckCircle2 className="w-3.5 h-3.5 text-lime-300 mt-1 flex-shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function ProblemBullet({ children, testid }) {
  return (
    <div className="flex gap-2 items-start text-[13px] leading-relaxed text-white/75" data-testid={testid}>
      <AlertCircle className="w-3.5 h-3.5 text-rose-300 mt-1 flex-shrink-0" />
      <span>{children}</span>
    </div>
  );
}

function RevLayer({ n, title, detail, take, testid }) {
  return (
    <div className="flex items-start gap-4 rounded-xl border border-white/10 bg-white/[0.02] p-4 hover:border-lime-400/30 transition-colors" data-testid={testid}>
      <div className="shrink-0 w-7 h-7 rounded-md bg-lime-400/15 border border-lime-400/30 grid place-items-center text-lime-300 text-[12px] font-semibold tabular-nums">{n}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-[13.5px] font-semibold text-white">{title}</div>
          <div className="text-[11px] text-lime-300 font-medium tabular-nums whitespace-nowrap">{take}</div>
        </div>
        <p className="mt-1 text-[12px] leading-relaxed text-white/60">{detail}</p>
      </div>
    </div>
  );
}

function MCell({ state }) {
  if (state === "y") return <CheckCircle2 className="w-4 h-4 text-lime-300 mx-auto" />;
  if (state === "p") return <CircleDot className="w-3 h-3 text-amber-400 mx-auto" />;
  return <span className="text-white/25">—</span>;
}

export default function Pitch() {
  useEffect(() => { document.title = "verqo — The operating system for wholesale"; }, []);

  return (
    <div className="min-h-screen bg-[#0a0e0a] text-white antialiased" data-testid="pitch-deck" id="top">
      {/* Iter 245 · Sticky section index so investors can jump between slides */}
      <PitchIndex />

      {/* Iter 246 · Print-only running footer — renders on every PDF page via
          `position: fixed` in print media. Adds a professional bottom-bar
          brand strip (verqo · confidential · tagline) so no slide looks
          "unfinished" when the section content is shorter than the page. */}
      <div className="pitch-print-footer" aria-hidden="true">
        <span className="pitch-print-footer-left">verqo &nbsp;·&nbsp; Confidential &nbsp;·&nbsp; 2026</span>
        <span className="pitch-print-footer-center">The operating system for wholesale</span>
        <span className="pitch-print-footer-right">wholesale, instant.</span>
      </div>

      {/* Subtle grain background */}
      <div
        className="fixed inset-0 opacity-[0.04] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")",
        }}
      />

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* PAGE 1 — Problem · Solution · Why us                              */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen px-8 md:px-20 pt-16 md:pt-20 pb-12 md:pb-16 max-w-[1280px] mx-auto scroll-mt-14" data-testid="page-1" id="cover">
        {/* Page header removed Iter 245 · unified sticky nav (PitchIndex) is
            the single header for the whole deck. Cover slide starts direct
            with the eyebrow → title. */}
        {/* Hero — OS positioning. Lead with surfaces + apps + APIs, not
            payment/back-office verbs. The deck used to read like a finance
            stack; the explicit "operating system" frame fixes that. */}
        <Eyebrow testid="hero-eyebrow">The operating system for wholesale</Eyebrow>
        <h1 className="mt-5 text-[44px] md:text-[68px] leading-[0.95] font-semibold tracking-tight text-white">
          Everything wholesale needs to<br />
          <span className="text-lime-300">sell, get paid, and ship</span>
          <span className="text-white/55 italic"> — in one platform.</span>
        </h1>
        <p className="mt-7 max-w-2xl text-[15px] md:text-[16px] leading-relaxed text-white/70">
          Buyers, sellers, lenders and insurers <span className="text-white">run their day-to-day on verqo</span> —
          catalogs, orders, financing, shipping and disputes — all on one fabric, with
          installable apps and open APIs so anyone can extend the platform.
        </p>

        {/* Iter 242 · Cover contact footer — Series Seed · deck identity */}
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11.5px] mono uppercase tracking-[0.18em] text-white/45" data-testid="hero-contact-footer">
          <span>verqo.com</span>
          <span>· Series Seed · 2026</span>
          <span className="text-white/70">· The operating system for wholesale</span>
        </div>

        {/* Daily-workflow proof tiles — borrowed from the landing hero so the
            deck shows the OS is alive, not slideware. */}
        <div className="mt-9 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hero-live-tiles">
          {[
            { l: "AI credit memo · Demo Wholesale Co.", v: "Net-30 $50K · 94%", tone: "lime" },
            { l: "ACH transfer · settled",              v: "+$12,400",           tone: "lime" },
            { l: "Verqo Shipping label · USPS",                 v: "in transit",         tone: "muted" },
            { l: "Plaid · cashflow refresh",            v: "✓ 2s ago",           tone: "lime" },
          ].map((t, i) => (
            <div key={i} className={`rounded-xl border ${t.tone === "lime" ? "border-lime-400/25 bg-lime-400/[0.04]" : "border-white/10 bg-white/[0.02]"} p-3.5`}>
              <div className="text-[9.5px] mono uppercase tracking-[0.2em] text-white/55">{t.l}</div>
              <div className="mt-1 text-[14px] font-semibold tracking-tight text-white tabular-nums">{t.v}</div>
            </div>
          ))}
        </div>

        {/* The Problem */}
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 scroll-mt-14" id="problem">
          <div>
            <Eyebrow testid="problem-eyebrow">The problem</Eyebrow>
            <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight">
              $32 trillion of B2B trade still runs on 1980s plumbing.
            </h2>

            {/* Iter 242 · "Uncomfortable numbers" strip — hard stats that
                make the pain physically felt before the bullets soften it. */}
            <div className="mt-5 grid grid-cols-3 gap-2" data-testid="problem-numbers">
              {[
                { v: "17h",   l: "wasted per week per wholesale ops team switching between 7+ tools" },
                { v: "44%",   l: "of B2B invoices are paid late — average lateness is 21 days" },
                { v: "$3.1T", l: "trapped in accounts receivable across US wholesale annually" },
              ].map((s, i) => (
                <div key={i} className="rounded-lg border border-rose-400/20 bg-rose-500/[0.05] p-3">
                  <div className="text-[24px] md:text-[28px] font-semibold tracking-tight text-rose-200 tabular-nums leading-none">{s.v}</div>
                  <div className="mt-2 text-[10.5px] leading-snug text-white/60">{s.l}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 space-y-3" data-testid="problems">
              <ProblemBullet testid="p-cashflow">
                <strong className="text-white">Sellers wait 60-90 days</strong> for invoices to settle — their cash is frozen, their inventory turn collapses.
              </ProblemBullet>
              <ProblemBullet testid="p-trust">
                <strong className="text-white">Buyers can't get credit fast.</strong> Manual KYB, paper trade-references, spreadsheet credit decisions, and ghosting risk on both sides.
              </ProblemBullet>
              <ProblemBullet testid="p-stack">
                <strong className="text-white">Wholesalers stitch 7+ vendors</strong> — QuickBooks · Shopify · Stripe · a BNPL · a freight carrier · an insurer · a sponsor bank — none talk to each other.
              </ProblemBullet>
              <ProblemBullet testid="p-compliance">
                <strong className="text-white">Compliance is brutal.</strong> ECOA · BSA/AML · OFAC · NACHA · GLBA · SR 11-7 · 1071 — small wholesalers literally cannot afford to be compliant.
              </ProblemBullet>
              <ProblemBullet testid="p-incumbents">
                <strong className="text-white">Incumbents pick a side.</strong> The big-five B2B BNPLs (TreviPay, Resolve, Apruve, Two, Hokodo) serve buyers <em>or</em> sellers — never both — and none of them own the rails.
              </ProblemBullet>
            </div>
          </div>

          {/* The Solution — 5-pillar grid replaces the prior bullet list.
              Pillar #5 ("Apps · Extensible") is what flips the perception
              from "back-office finance stack" to "operating system". */}
          <div>
            <Eyebrow testid="solution-eyebrow">What we've already shipped</Eyebrow>
            <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight text-lime-100">
              A wholesale OS that <span className="text-lime-300">already works.</span>
            </h2>
            <div className="mt-5 grid grid-cols-2 gap-2.5" data-testid="solution-pillars">
              {[
                { Icon: CreditCard,  l: "Payments",   d: "Cards, ACH, wire — settled into a real ledger.",        m: "Verqo Pay · Stripe" },
                { Icon: Banknote,    l: "Credit",     d: "AI-underwritten Net-30 to Net-90 in 60 seconds.",       m: "Plaid · Middesk · verqo AI" },
                { Icon: Truck,       l: "Shipping",   d: "Parcel + LTL/FTL freight from one API.",                m: "Verqo Freight Network" },
                { Icon: ShieldCheck, l: "Insurance",  d: "Trade credit insurance baked into checkout.",           m: "Allianz Trade" },
                { Icon: AppWindow,   l: "Apps · Extensible", d: "Catalog, Dropship, RFQ, ERP — installable per portal. Anyone can build more.", m: "App Store · OAuth scopes", wide: true },
              ].map(({ Icon, l, d, m, wide }) => (
                <div key={l}
                  className={`${wide ? "col-span-2" : ""} group rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:border-lime-400/40 transition-colors`}
                  data-testid={`solution-${l.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
                  <div className="flex items-center justify-between">
                    <Icon className="w-4 h-4 text-lime-300" />
                    <span className="text-[9px] mono uppercase tracking-[0.18em] text-white/40">{m}</span>
                  </div>
                  <div className="mt-3 text-[14px] font-semibold tracking-tight text-white">{l}</div>
                  <p className="mt-1 text-[12px] leading-relaxed text-white/65">{d}</p>
                </div>
              ))}
            </div>
            {/* Compliance & bank-rail moat compressed into a single trust line
                so the deck doesn't read like a RegTech / examiner-portal pitch. */}
            <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-3.5 text-[11.5px] text-white/55 leading-relaxed" data-testid="trust-footnote">
              <span className="text-lime-300/80 mono uppercase tracking-[0.18em] text-[10px] mr-2">Trust layer</span>
              Bank-grade compliance (BSA/AML · OFAC · ECOA · NACHA · SR 11-7), 16-rule transaction monitoring and a 37-page examiner portal ship as defaults. Compliance is a feature of the OS, not the pitch.
            </div>
          </div>
        </div>

        {/* Iter 242 · Workflow narrative — the elevator pitch made visual.
            Investors get it in 6 seconds: "one path for every transaction". */}
        <div className="mt-16 rounded-2xl border border-lime-400/20 bg-lime-400/[0.03] p-6 md:p-8 scroll-mt-14" data-testid="workflow-narrative" id="product">
          <Eyebrow testid="workflow-eyebrow">The workflow</Eyebrow>
          <h3 className="mt-3 text-[24px] md:text-[32px] leading-[1.05] font-semibold tracking-tight max-w-3xl">
            Every wholesale transaction — <span className="text-lime-300">one path</span>, no tab-switching.
          </h3>
          <p className="mt-3 max-w-3xl text-[13.5px] leading-relaxed text-white/65">
            Today, a retailer buying inventory juggles emails, PDFs, phone calls, ACH instructions,
            shipping brokers, insurance companies, and lenders — 7 tools that don&apos;t talk. With Verqo,
            every step of that transaction happens inside one connected flow:
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="workflow-chain">
            {[
              { l: "Discover", d: "supplier" },
              { l: "Place",    d: "order" },
              { l: "Pay",      d: "securely" },
              { l: "Finance",  d: "if needed" },
              { l: "Insure",   d: "the shipment" },
              { l: "Book",     d: "freight" },
              { l: "Track",    d: "delivery" },
              { l: "Reconcile",d: "invoices" },
            ].map((s, i, arr) => (
              <div key={s.l} className="flex items-center gap-2">
                <div className="rounded-lg border border-white/15 bg-white/[0.04] px-3 py-2">
                  <div className="text-[12.5px] font-semibold text-white leading-none">{s.l}</div>
                  <div className="text-[9.5px] mono uppercase tracking-[0.14em] text-white/50 mt-1">{s.d}</div>
                </div>
                {i < arr.length - 1 && <span className="text-lime-300/60 text-sm">→</span>}
              </div>
            ))}
          </div>
          <div className="mt-6 text-[12.5px] text-white/70">
            <span className="text-white font-semibold">All in one place.</span> Every step. Every transaction. Every dollar.
            The rest of the stack — Shopify, Stripe, Flexport, TreviPay, Allianz — solves a slice; Verqo is the whole picture.
          </div>
        </div>

        {/* ── NEW · OS positioning block: "One backbone. Every workflow."
            Borrowed from the landing page so the deck visually shows the
            scope of the platform (not just credit + payments). ──────── */}
        <div className="mt-16" data-testid="one-backbone">
          <Eyebrow testid="backbone-eyebrow">The platform</Eyebrow>
          <h3 className="mt-4 text-2xl md:text-4xl font-semibold tracking-tight max-w-3xl">
            One backbone. <span className="text-white/55 italic">Every workflow.</span>
          </h3>
          <p className="mt-3 max-w-2xl text-[13.5px] leading-relaxed text-white/60">
            From buyer signup to seller payout — orders, invoices, financing, shipping, insurance and reporting all share one identity layer, one ledger, one source of truth. Every surface (buyer, seller, lender, insurer, bank) reads from the same row in real time.
          </p>
          <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-2.5" data-testid="backbone-grid">
            {[
              { l: "Orders",     v: "Unified" },
              { l: "Invoicing",  v: "Auto-generated" },
              { l: "Wallet",     v: "Ledger-backed" },
              { l: "Credit",     v: "AI-underwritten" },
              { l: "Identity",   v: "KYB + KYC" },
              { l: "Shipping",   v: "Smart-routed" },
              { l: "Insurance",  v: "Embedded" },
              { l: "Apps",       v: "Extensible", lime: true },
            ].map(({ l, v, lime }) => (
              <div key={l} className={`rounded-2xl border ${lime ? "border-lime-400/40 bg-lime-400/[0.05]" : "border-white/10 bg-white/[0.02]"} px-4 py-5`}>
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-white/50">{l}</div>
                <div className={`mt-2 text-lg font-semibold tracking-tight ${lime ? "text-lime-300" : "text-white"}`}>{v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── NEW · "Build on verqo" — App Store / extensibility block.
            This is the single biggest perception flip from "accounting"
            to "operating system" — investors recognise this pattern
            (Shopify App Store, Stripe Apps, Slack Apps). ──────────── */}
        <div className="mt-16 rounded-2xl border border-white/10 bg-white/[0.02] p-7 md:p-9" data-testid="build-on-verqo">
          <Eyebrow testid="build-eyebrow">Build on verqo</Eyebrow>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 items-start">
            <div>
              <h3 className="text-2xl md:text-4xl font-semibold tracking-tight">
                We're the <span className="text-lime-300">rails.</span><br />
                <span className="text-white/55 italic">You build the train.</span>
              </h3>
              <p className="mt-5 text-[14px] leading-relaxed text-white/70">
                verqo ships the core — payments, credit, shipping, insurance, identity, ledger.
                Apps run inside each portal and extend it: dropship cockpit, RFQ desk, custom catalogs, QuickBooks/Shopify/EDI sync, ERP connectors, AI agents — anything wholesalers need beyond the core.
              </p>
              <div className="mt-6 space-y-2.5 text-[12.5px]" data-testid="build-bullets">
                {[
                  { Icon: Code2,     t: "Public REST + Webhook APIs for every domain object" },
                  { Icon: AppWindow, t: "App Store install model · apps run inside each portal" },
                  { Icon: Plug,      t: "OAuth scopes per app · revoke in one click" },
                  { Icon: Boxes,     t: "Private apps for one team or publish to the marketplace" },
                ].map(({ Icon, t }) => (
                  <div key={t} className="flex items-start gap-2.5">
                    <Icon className="w-3.5 h-3.5 mt-1 text-lime-300 shrink-0" />
                    <span className="text-white/80">{t}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* App Store mock — keeps the same dark-charcoal aesthetic and
                signals "this is a platform with installable apps" at a
                glance, the way the website does. */}
            <div className="rounded-2xl border border-lime-400/15 bg-[#0c130c] p-5" data-testid="app-store-mock">
              <div className="flex items-center justify-between">
                <div className="text-[10px] mono uppercase tracking-[0.2em] text-lime-300">verqo · app store</div>
                <div className="text-[10px] mono text-white/40">v1 · live</div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {[
                  { l: "Catalog",     d: "AI importer · CSV · ZIP · API",      installed: true },
                  { l: "Dropship",    d: "Shopify ↔ supplier · auto-route",     installed: true },
                  { l: "Messages",    d: "Buyer ↔ seller threads · attachments", installed: true },
                  { l: "RFQ",         d: "Multi-quote desk · pricing tiers",     installed: false },
                  { l: "QuickBooks",  d: "Invoice sync · GL push",               installed: false },
                  { l: "Shopify",     d: "Orders + payouts · 1-click",           installed: true },
                ].map(({ l, d, installed }) => (
                  <div key={l} className="rounded-lg border border-white/10 bg-white/[0.02] p-3 hover:border-lime-400/40 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="text-[12px] font-semibold text-white">{l}</div>
                      {installed
                        ? <span className="text-[9px] mono uppercase tracking-[0.16em] text-lime-300">Installed</span>
                        : <span className="text-[9px] mono uppercase tracking-[0.16em] text-white/45">+ Install</span>}
                    </div>
                    <p className="mt-1 text-[10.5px] text-white/55 leading-tight">{d}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-[10.5px] text-white/45 leading-relaxed">
                Internal devs, partners, and the community all publish here. Each app inherits the same identity, ledger, and policy gate — zero auth-plumbing required.
              </div>
            </div>
          </div>
        </div>

        {/* ── Manifesto callout — preserves the previous hero line as a
            poster-style act-break between the OS positioning blocks and
            the "Why instant for both sides" flywheel. ──────────────── */}
        <div className="mt-16 relative overflow-hidden rounded-2xl border border-lime-400/30 bg-[#0c130c] px-8 md:px-12 py-12 md:py-16" data-testid="manifesto">
          <div className="absolute -right-24 -top-24 w-64 h-64 rounded-full bg-lime-400/10 blur-3xl pointer-events-none" />
          <div className="absolute -left-20 -bottom-20 w-56 h-56 rounded-full bg-lime-400/[0.07] blur-3xl pointer-events-none" />
          <div className="relative">
            <Eyebrow testid="manifesto-eyebrow">The manifesto</Eyebrow>
            <h2 className="mt-5 text-[40px] md:text-[64px] leading-[0.95] font-semibold tracking-tight text-white max-w-4xl">
              Wholesale is stuck in <span className="text-white/45 italic">paperwork.</span>
              <br />
              We're rebuilding it <span className="text-lime-300">instant.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-[14px] md:text-[15px] leading-relaxed text-white/65">
              SMB buyers get instant credit. SMB sellers get paid same-day. Underwritten by AI, insured by Allianz Trade, settled over real-bank rails — and policed by bank-grade compliance that examiners actually trust. That's the OS doing its job underneath every workflow above.
            </p>
          </div>
        </div>

        {/* Why instant payment for BOTH sides */}
        <div className="mt-16 rounded-2xl border border-lime-400/15 bg-gradient-to-br from-lime-400/5 to-transparent p-8" data-testid="why-instant">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 items-center">
            <div>
              <Eyebrow>For the seller</Eyebrow>
              <h3 className="mt-3 text-xl md:text-2xl font-semibold tracking-tight">Get paid <span className="text-lime-300">Day-0.</span></h3>
              <p className="mt-3 text-[13px] leading-relaxed text-white/65">
                Same-day disbursement on every invoice. Re-buy inventory the same week instead of next quarter.
                Inventory turn improves 5-6x. Working capital is no longer the ceiling on growth.
              </p>
            </div>

            <ArrowRight className="hidden lg:block w-10 h-10 text-lime-400/70" />
            <div className="lg:hidden h-px bg-lime-400/20" />

            <div>
              <Eyebrow>For the buyer</Eyebrow>
              <h3 className="mt-3 text-xl md:text-2xl font-semibold tracking-tight">Pay <span className="text-lime-300">Net-60</span>, frictionlessly.</h3>
              <p className="mt-3 text-[13px] leading-relaxed text-white/65">
                One credit line across every supplier. No spreadsheets · no late fees · no re-underwriting · no paper trade references.
                Approved in seconds. Insured by Allianz. Honored everywhere.
              </p>
            </div>
          </div>
          <div className="mt-7 pt-5 border-t border-lime-400/15 text-[12.5px] text-white/55 italic">
            Wholesale is a two-sided liquidity problem. Fix only the buyer, sellers starve. Fix only the seller, buyers churn. <strong className="text-white">Fix both at once and the network compounds.</strong>
          </div>
        </div>

        {/* Buyer visibility + Buyer Score — the seller's secret weapon */}
        <div className="mt-16" data-testid="buyer-score">
          <Eyebrow testid="buyer-score-eyebrow">The seller's secret weapon</Eyebrow>
          <h3 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight max-w-3xl">
            Sellers stop gambling on new buyers. They see a <span className="text-lime-300">live Buyer Score</span> before they ship.
          </h3>
          <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Pillar testid="bs-visibility" icon={Globe} title="Buyer visibility" body="Every buyer carries a real-time profile — order cadence, repeat-purchase pattern, demand signals, and network-wide repayment behavior. Sellers court the right buyers instead of guessing." />
            <Pillar testid="bs-score" icon={TrendingUp} title="AI Buyer Score v4" body="One portable creditworthiness score from AI underwriting + behavioral + repayment data across the whole network. A buyer who pays on time at one seller is instantly trusted at the next — zero re-underwriting." />
            <Pillar testid="bs-derisk" icon={ShieldCheck} title="Zero-loss terms" body="The score sets the limit; verqo + Allianz Trade carry the default. Sellers extend Net-60 with confidence — say yes to more buyers, bigger orders, faster. Visibility converts straight into GMV." />
          </div>
          <div className="mt-6 rounded-xl border border-lime-400/20 bg-lime-400/[0.03] p-5 text-[12.5px] text-white/70" data-testid="bs-flywheel">
            <strong className="text-white">The flywheel:</strong> more buyers → richer Buyer Scores → sellers extend more credit → buyers transact more → scores sharpen again. The data moat compounds with every order and is impossible to buy off the shelf.
          </div>
        </div>

        {/* ═══ Iter 242 · Founder-market fit + Team ═══════════════════════ */}
        <div className="mt-16 pt-10 border-t border-white/5 scroll-mt-14" data-testid="team-slide" id="team">
          <Eyebrow testid="team-eyebrow">Why us</Eyebrow>
          <h2 className="mt-3 text-[32px] md:text-[44px] leading-[0.98] font-semibold tracking-tight">
            Built by the people who <span className="text-lime-300">carried this pain</span> on their own P&amp;L.
          </h2>
          <p className="mt-4 max-w-3xl text-[14px] md:text-[15px] leading-relaxed text-white/65">
            Verqo wasn&apos;t built from theory. Every founder-market-fit signal is real:
            operators who processed millions in wholesale transactions, managed thousands of
            SKUs, worked with suppliers across three continents — and lived every single
            inefficiency we now solve.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="team-grid">
            {[
              { name: "Zev Ostreicher", role: "Founder & CEO",
                bio: "Sets product direction, capital strategy, and partnerships. Years in wholesale operations — built Verqo to solve problems he carried on his own P&L.",
                tag: "Founder-market fit" },
              { name: "David Levy", role: "Head of Operations",
                bio: "Runs day-to-day operations: onboarding, compliance workflows, customer success. Keeps the platform shipping and partners happy.",
                tag: "Ops leadership" },
              { name: "Basi Hershkowitz", role: "Sales Director",
                bio: "Owns the seller and buyer GTM funnel. Walks brands and retailers through the Verqo flow and the financial mechanics behind it.",
                tag: "Revenue leadership" },
              { name: "Dom Byno", role: "Data & Automation Expert",
                bio: "Owns data, automation, and infrastructure. Designs underwriting models, reconciliation logic, and every integration behind the platform.",
                tag: "Data & Eng" },
            ].map(m => (
              <div key={m.name}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-4"
                data-testid={`team-${m.name.split(" ")[0].toLowerCase()}`}>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full bg-lime-400/15 border border-lime-400/30 grid place-items-center flex-shrink-0">
                    <span className="text-lime-300 font-semibold text-sm">
                      {m.name.split(" ").map(w => w[0]).join("")}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[15px] font-semibold text-white">{m.name}</div>
                    <div className="text-[11.5px] mono uppercase tracking-[0.14em] text-lime-300/80 mt-0.5">{m.role}</div>
                    <p className="mt-2 text-[12.5px] leading-relaxed text-white/60">{m.bio}</p>
                    <span className="mt-2 inline-block text-[9.5px] mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-white/10 text-white/50">{m.tag}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] p-4 text-[12.5px] leading-relaxed text-white/60" data-testid="team-lean">
            <span className="text-white">Lean by design.</span> Additional hires (Risk &amp; Underwriting,
            BSA Officer, Engineering) come online alongside sponsor-bank onboarding — the
            $10M standby capital is committed to bring the right people on at the right moment.
          </div>
        </div>

        {/* ═══ Iter 243 · Projected Trajectory (from Y1 P&L model) ═════════
            Replaced Iter 242 placeholder KPIs with actual quarterly ramp
            from the internal unit-economics workbook. Model window:
            Jul 2026 → Jun 2027. All figures cited to the model. */}
        <div className="mt-16 pt-10 border-t border-white/5 scroll-mt-14" data-testid="traction-slide" id="traction">
          <Eyebrow testid="traction-eyebrow">Projected trajectory · Y1</Eyebrow>
          <h2 className="mt-3 text-[32px] md:text-[44px] leading-[0.98] font-semibold tracking-tight">
            A <span className="text-lime-300">$863M bank-cleared</span> Y1 — built bottom-up.
          </h2>
          <p className="mt-4 max-w-3xl text-[14px] md:text-[15px] leading-relaxed text-white/65">
            The 12-month forecast is a bottom-up P&amp;L, not a top-down TAM slice. Every quarter
            models real onboarding cadence, seller mix, and buyer activation rates from Verqo&apos;s
            operating funnel. Model window: <span className="text-white">Jul 2026 → Jun 2027</span>.
          </p>

          {/* Y1 headline KPIs */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="traction-kpis">
            {[
              { v: "120",     l: "Sellers onboarded",       s: "active EOY1 · wholesale + dropship" },
              { v: "300",     l: "Buyers onboarded",        s: "avg 2.5 buyers per seller" },
              { v: "$256M",   l: "GMV · Y1 total",          s: "gross merchandise volume" },
              { v: "$863M",   l: "Bank-cleared · Y1",       s: "settled through U.S. Bank rails" },
            ].map((k, i) => (
              <div key={i} className="rounded-2xl border border-lime-400/25 bg-lime-400/[0.04] p-4">
                <div className="text-[28px] md:text-[32px] font-semibold tracking-tight text-white tabular-nums leading-none">{k.v}</div>
                <div className="mt-2 text-[11.5px] font-medium text-white/85">{k.l}</div>
                <div className="mt-1 text-[10px] mono uppercase tracking-[0.14em] text-white/45">{k.s}</div>
              </div>
            ))}
          </div>

          {/* Quarterly ramp table */}
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5 overflow-hidden" data-testid="traction-quarterly">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-white/50 mb-3">Quarterly ramp · from the P&amp;L model</div>
            <div className="overflow-x-auto -mx-1 px-1">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-[10.5px] mono uppercase tracking-[0.14em] text-white/45 border-b border-white/10">
                    <th className="text-left font-normal py-2 pr-3">Metric</th>
                    <th className="text-right font-normal py-2 px-3">Q1</th>
                    <th className="text-right font-normal py-2 px-3">Q2</th>
                    <th className="text-right font-normal py-2 px-3">Q3</th>
                    <th className="text-right font-normal py-2 pl-3">Q4</th>
                  </tr>
                </thead>
                <tbody className="text-white/80 tabular-nums">
                  {[
                    { m: "Sellers onboarded", q: ["14", "18", "22", "26"] },
                    { m: "Buyers onboarded",  q: ["45", "50", "60", "70"] },
                    { m: "GMV",               q: ["$13.0M", "$23.8M", "$43.9M", "$63.7M"] },
                    { m: "Revenue",           q: ["$3.4M", "$12.8M", "$31.1M", "$45.6M"] },
                    { m: "ARR run-rate",      q: ["$1.5M", "$3.2M", "$6.4M", "$9.6M"] },
                    { m: "Bank-cleared vol.", q: ["$17.7M", "$87.2M", "$162.5M", "$311.2M"] },
                  ].map(r => (
                    <tr key={r.m} className="border-b border-white/5 last:border-0">
                      <td className="py-2 pr-3 text-white/60">{r.m}</td>
                      {r.q.map((v, i) => (
                        <td key={i} className={`text-right py-2 ${i === 0 ? "px-3" : i === 3 ? "pl-3 text-lime-300 font-medium" : "px-3"}`}>{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Unit economics inset */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="traction-unit-econ">
            {[
              { l: "Take rate · Y1",     v: "6.0%",   s: "blended across all revenue lines" },
              { l: "Gross margin",       v: "98.6%",  s: "high-margin SaaS + take-rate model" },
              { l: "LTV : CAC",          v: "~18:1",  s: "buyer-side viral acquisition helps" },
              { l: "Revenue lines",      v: "8",      s: "compounding on the same GMV" },
            ].map((k, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
                <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-white/50">{k.l}</div>
                <div className="mt-1.5 text-[20px] md:text-[22px] font-semibold text-white tabular-nums leading-none">{k.v}</div>
                <div className="mt-1 text-[10.5px] text-white/55">{k.s}</div>
              </div>
            ))}
          </div>

          {/* Acquisition channels — Iter 246 · Promoted to its own labeled
              mini-slide (Eyebrow "GTM · Acquisition") so it lands on its
              own PDF page as a substantive story, not as spillover from
              the Traction P&L. Investors read GTM as a distinct topic. */}
          <div className="mt-8 pt-6 border-t border-white/5" data-testid="traction-acquisition">
            <Eyebrow testid="acquisition-eyebrow">GTM · How we win the first 300 buyers</Eyebrow>
            <h3 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight max-w-3xl">
              Three <span className="text-lime-300">compounding acquisition channels.</span>
            </h3>
            <p className="mt-3 max-w-2xl text-[13.5px] leading-relaxed text-white/60">
              Growth doesn't rely on paid CAC alone. Direct outbound reaches ICP fast,
              partnerships convert warm inbound at a fraction of the cost, and every
              seller-buyer transaction pulls new buyers in for free.
            </p>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { l: "Acquisition · direct", cac: "$2,800 CAC", d: "Outbound to top-100 wholesale brands per vertical. Target 22% booked-meeting rate, 18% close on booked-meetings. Payback ~8 months." },
                { l: "Acquisition · partnerships", cac: "$1,100 CAC", d: "3PLs, ERPs, and trade associations refer pre-qualified sellers with existing GMV. Rev-share on months 1–12 keeps partners incentivized." },
                { l: "Acquisition · viral loop", cac: "$0 CAC", d: "Every seller invites their buyers; each buyer sees Verqo checkout in the wild. Compounds — the 30th seller costs a fraction of the 1st." },
              ].map(x => (
                <div key={x.l} className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] mono uppercase tracking-[0.16em] text-lime-300/80">{x.l}</div>
                    <div className="text-[10.5px] mono tabular-nums text-white/85">{x.cac}</div>
                  </div>
                  <p className="mt-2 text-[12.5px] leading-relaxed text-white/65">{x.d}</p>
                </div>
              ))}
            </div>

            {/* Iter 246 · Blended acquisition math strip — turns the slide
                from "here are three cards" into a real investor takeaway. */}
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="acquisition-math">
              {[
                { l: "Blended CAC · Y1",   v: "$1,600" },
                { l: "LTV : CAC",          v: "~18:1" },
                { l: "Payback period",     v: "6.4 mo" },
                { l: "% via viral loop",   v: "38%" },
              ].map(m => (
                <div key={m.l} className="rounded-xl border border-lime-400/20 bg-lime-400/[0.04] p-3.5">
                  <div className="text-[10px] mono uppercase tracking-[0.16em] text-white/55">{m.l}</div>
                  <div className="mt-1 text-[22px] font-semibold text-white tabular-nums leading-none">{m.v}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-4 text-[12.5px] leading-relaxed text-white/65" data-testid="acquisition-narrative">
              <span className="text-white font-semibold">Why this compounds:</span> The viral loop is the moat.
              Every seller onboarded pulls ~30 buyers into Verqo's checkout for free. Those buyers, once
              approved, are portable across the network — so seller #100 unlocks buyers seller #1 could
              never reach. CAC drops as the network expands.
            </div>
          </div>

          <div className="mt-4 text-[9.5px] mono uppercase tracking-[0.2em] text-white/35" data-testid="traction-source">
            · figures from internal 12-month unit-economics model · projections, not guarantees ·
          </div>
        </div>

        <footer className="mt-14 flex items-center justify-between text-[10.5px] uppercase tracking-[0.25em] text-white/30">
          <span>verqo · the operating system for wholesale · wholesale, instant</span>
          <span>page 1</span>
        </footer>
      </section>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* PAGE 2 — Market · Why now · Future · Why #1                        */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen px-8 md:px-20 py-12 md:py-16 max-w-[1280px] mx-auto border-t border-white/5" data-testid="page-2">
        {/* Page 2 header removed Iter 245 · sticky PitchIndex is the single deck header. */}

        {/* ═══ Iter 242 · Why Now ══════════════════════════════════════════
            Market timing catalysts — the "why this moment" that turns a
            good idea into an urgent bet. */}
        <div className="mb-16 scroll-mt-14" data-testid="why-now-slide" id="why-now">
          <Eyebrow testid="why-now-eyebrow">Why now</Eyebrow>
          <h2 className="mt-4 text-[38px] md:text-[52px] leading-[0.98] font-semibold tracking-tight">
            Wholesale is having its <span className="text-lime-300">Shopify moment.</span>
          </h2>
          <p className="mt-5 max-w-3xl text-[14px] md:text-[15px] leading-relaxed text-white/65">
            For the first time, the four ingredients required to build a true operating
            system for wholesale are all mature at the same time. Verqo is the platform
            wholesale has been waiting for — and the window to capture it is now.
          </p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="why-now-catalysts">
            {[
              { l: "AI underwriting", d: "60-second credit decisions with default rates matching legacy 30-day paper trails. Impossible 3 years ago." },
              { l: "Embedded finance", d: "Sponsor-bank rails, insurance APIs, and BNPL infrastructure — now productized and composable." },
              { l: "Real-time payments", d: "RTP + FedNow flipped the assumption that B2B has to be Net-anything. Same-day settlement is table stakes." },
              { l: "Modern APIs", d: "Every ERP, warehouse, catalog, and marketplace now exposes clean APIs. The plumbing is finally there." },
            ].map(c => (
              <div key={c.l} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="text-[11.5px] mono uppercase tracking-[0.16em] text-lime-300/80">{c.l}</div>
                <p className="mt-3 text-[12.5px] leading-relaxed text-white/65">{c.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-xl border border-lime-400/20 bg-lime-400/[0.04] p-5 text-[13px] leading-relaxed text-white/75" data-testid="why-now-thesis">
            <span className="text-white font-semibold">The parallel:</span> Shopify launched in 2006 when payments,
            open-source, and hosted retail infra all matured simultaneously. Amazon Business, TreviPay, and Resolve are
            all point products — none of them is the platform. Wholesale is a $32T market waiting for its Shopify.
          </div>
        </div>

        {/* Market gap */}
        <div className="scroll-mt-14" id="market">
        <Eyebrow testid="market-eyebrow">The market</Eyebrow>
        <h2 className="mt-4 text-[38px] md:text-[56px] leading-[0.98] font-semibold tracking-tight">
          A $32T market.<br />
          <span className="text-white/45">Only 7% digitized.</span>
        </h2>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10" data-testid="market-stats">
          <Stat value="$32T" label="Global B2B trade volume" testid="stat-tam" />
          <Stat value="$1.45T" label="US B2B BNPL TAM by 2028" testid="stat-bnpl" />
          <Stat value="26%" label="CAGR B2B BNPL · 2024-28" testid="stat-cagr" />
          <Stat value="~7%" label="Of B2B trade fully digitized" testid="stat-digital" />
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-[13px] leading-relaxed text-white/65" data-testid="market-pulls">
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
            <div className="text-lime-300 text-[11px] uppercase tracking-[0.18em] mb-2">Seller pull</div>
            <strong className="text-white text-lg">89%</strong>
            <p className="mt-1">of SMB suppliers cite <strong className="text-white">slow payments</strong> as their #1 operational risk.</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
            <div className="text-lime-300 text-[11px] uppercase tracking-[0.18em] mb-2">Buyer pull</div>
            <strong className="text-white text-lg">64%</strong>
            <p className="mt-1">of SMB buyers say they'd <strong className="text-white">buy more, more often</strong> if Net-60 was instant.</p>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
            <div className="text-lime-300 text-[11px] uppercase tracking-[0.18em] mb-2">Incumbent gap</div>
            <strong className="text-white text-lg">0 of 5</strong>
            <p className="mt-1">top US B2B BNPLs serve <strong className="text-white">both sides natively</strong> with a unified ledger.</p>
          </div>
        </div>
        </div>

        {/* Why all-in-one matters */}
        <div className="mt-14">
          <Eyebrow testid="next-gen-eyebrow">Why all-in-one is the wholesale platform</Eyebrow>
          <h3 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight max-w-3xl">
            Wholesalers don't need <span className="text-white/40">seven point-tools.</span> They need <span className="text-lime-300">one operating system.</span>
          </h3>
          <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Pillar testid="pillar-ledger" icon={Layers} title="One ledger" body="Every PO, invoice, credit line, repayment, dispute, and shipment lives in a single source of truth. Buyer, seller, lender, insurer, and bank all see the same row in real time." />
            <Pillar testid="pillar-trust" icon={ShieldCheck} title="One trust layer" body="AI Buyer Score · Seller Trust · 16-rule AML · OFAC · ECOA · NACHA · SR 11-7 — applied automatically to every transaction. Compliance becomes a feature, not a tax." />
            <Pillar testid="pillar-rails" icon={Banknote} title="One money stack" body="Wallet, ACH, wire, card, Allianz cover, and lender financing — all addressed through a single API. No reseller middlemen, no fragmented reconciliation." />
            <Pillar testid="pillar-ai" icon={Sparkles} title="One AI brain" body="The same models that decide credit also detect fraud, predict breaches, route shipments, and parse purchase orders. Network effects compound with every transaction." />
          </div>
        </div>

        {/* Dropship network — a second GMV engine */}
        <div className="mt-16" data-testid="dropship">
          <Eyebrow testid="dropship-eyebrow">A second GMV engine</Eyebrow>
          <h3 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight max-w-3xl">
            Dropship, built in. Suppliers list once — <span className="text-lime-300">retailers sell everywhere.</span>
          </h3>
          <p className="mt-3 max-w-2xl text-[13.5px] leading-relaxed text-white/60">
            The same network that finances wholesale also powers dropshipping. Suppliers don't carry the retailer relationship; retailers don't carry inventory. verqo sits in the middle — moving catalog, orders, money, and markup automatically.
          </p>
          <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Pillar testid="ds-sync" icon={Layers} title="Supplier catalog sync" body="Suppliers push their full catalog into the verqo network (and Shopify) in one click. Inventory and pricing stay live — no spreadsheets, no stale SKUs." />
            <Pillar testid="ds-route" icon={Network} title="1-click retailer routing" body="Retailers copy any supplier variant straight into their own Shopify store at their chosen markup. Zero inventory risk, infinite assortment." />
            <Pillar testid="ds-settle" icon={Zap} title="Auto-settled orders" body="Orders route back to the supplier; verqo handles fulfillment hand-off, payment, credit, and the markup split — every party paid automatically." />
          </div>
          <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="dropship-stats">
            <Stat value="+15%" label="Of sellers attach dropship" testid="ds-attach" />
            <Stat value="$50K/mo" label="Avg dropship GMV per seller" testid="ds-gmv" />
            <Stat value="$99/mo" label="Dropship-only plan" testid="ds-plan" />
            <Stat value="1-click" label="Supplier SKU → retailer storefront" testid="ds-speed" />
          </div>
          <div className="mt-6 rounded-xl border border-lime-400/20 bg-lime-400/[0.03] p-5 text-[12.5px] text-white/70" data-testid="ds-insight">
            <strong className="text-white">Why it compounds:</strong> dropship volume rides the exact rails, credit, and ledger we already built for wholesale — so every dropship order throws off the same eight revenue layers at near-zero marginal cost.
          </div>
        </div>

        {/* The profit engine — stacked revenue layers */}
        <div className="mt-16 scroll-mt-14" data-testid="revenue-engine" id="business">
          <Eyebrow testid="revenue-eyebrow">The profit engine</Eyebrow>
          <h3 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight max-w-3xl">
            One transaction. <span className="text-lime-300">Eight ways</span> we earn on it.
          </h3>
          <p className="mt-3 max-w-2xl text-[13.5px] leading-relaxed text-white/60">
            Every B2B-BNPL incumbent monetizes one thing — the financing spread. Because verqo owns the whole stack, the same GMV throws off <strong className="text-white">eight stacked revenue layers</strong>. Each layer de-risks the others, and our blended take rate lands well above any single-product competitor.
          </p>
          <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="revenue-layers">
            <RevLayer n="1" title="SaaS subscriptions" take="$99 → $1,999/mo" detail="Four-tier wholesale plans + $99/mo dropship + app add-ons. Predictable, high-margin ARR independent of GMV." testid="rev-saas" />
            <RevLayer n="2" title="Financing markup" take="~25% markup" detail="Verqo markup on every lender-funded credit line and Net-X term the buyer takes." testid="rev-financing" />
            <RevLayer n="3" title="Invoice factoring" take="~20% origination" detail="Sellers get 95% upfront; verqo keeps an origination cut of the factoring spread on each sold invoice." testid="rev-factoring" />
            <RevLayer n="4" title="Payment-rail spread" take="ACH · wire · RTP" detail="Margin between buyer pay-in fees and our bank pay-out cost, plus tiered card-surcharge passthrough." testid="rev-rails" />
            <RevLayer n="5" title="Card interchange" take="net interchange" detail="Net virtual-card interchange retained on card-funded purchases across the network." testid="rev-interchange" />
            <RevLayer n="6" title="Insurance commission" take="~0.5% insured GMV" detail="Allianz Trade pass-through commission on every credit dollar we insure." testid="rev-insurance" />
            <RevLayer n="7" title="Shipping margin" take="~10% commission" detail="Margin on verqo-arranged freight & parcel through embedded carrier rails." testid="rev-shipping" />
            <RevLayer n="8" title="Apps & Catalog API" take="seats + usage" detail="AI Assistant, QuickBooks/Shopify/EDI connectors, and the open Catalog API buyers plug their ERPs into." testid="rev-apps" />
          </div>
          <div className="mt-6 rounded-xl border border-lime-400/30 bg-lime-400/[0.04] p-5 text-[12.5px] text-white/80" data-testid="rev-insight">
            <strong className="text-white">Why investors lean in:</strong> stacking 8 monetization lines on one stream of GMV is structurally unavailable to point-tools — you have to own credit, rails, insurance, shipping, and the catalog at once. We do. That's a take rate competitors can't copy without rebuilding the entire stack.
          </div>
        </div>

        {/* Unit economics + trajectory */}
        <div className="mt-16" data-testid="unit-economics">
          <Eyebrow testid="econ-eyebrow">Unit economics & trajectory</Eyebrow>
          <h3 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight">
            The model already <span className="text-lime-300">pencils out.</span>
          </h3>
          <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="econ-scenarios">
            <div className="rounded-xl border border-white/10 p-5">
              <div className="text-white/40 text-[10.5px] uppercase tracking-[0.18em] mb-2">Bear · Year 1</div>
              <div className="text-3xl font-semibold tracking-tight text-white tabular-nums">$10–12M</div>
              <div className="text-[11px] text-white/50 mt-1">12-mo GMV</div>
            </div>
            <div className="rounded-xl border border-lime-400/30 bg-lime-400/[0.04] p-5">
              <div className="text-lime-300 text-[10.5px] uppercase tracking-[0.18em] mb-2">Base · Year 1</div>
              <div className="text-3xl font-semibold tracking-tight text-white tabular-nums">$28–30M</div>
              <div className="text-[11px] text-white/55 mt-1">12-mo GMV · ~$900K ARR run-rate · 110 sellers</div>
            </div>
            <div className="rounded-xl border border-white/10 p-5">
              <div className="text-white/40 text-[10.5px] uppercase tracking-[0.18em] mb-2">Bull · Year 1</div>
              <div className="text-3xl font-semibold tracking-tight text-white tabular-nums">$60–90M</div>
              <div className="text-[11px] text-white/50 mt-1">12-mo GMV · ~$2.16M ARR run-rate</div>
            </div>
          </div>
          <div className="mt-7 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="econ-metrics">
            <Stat value="≥ 4.5x" label="Target LTV / CAC by month 8" testid="econ-ltvcac" />
            <Stat value="8 layers" label="Revenue lines on the same GMV" testid="econ-layers" />
            <Stat value="~30 : 1" label="Buyers pulled in per seller" testid="econ-network" />
            <Stat value="2-sided" label="SaaS ARR + transactional take" testid="econ-blend" />
          </div>
          <p className="mt-5 text-[11px] text-white/35 italic">Illustrative Year-1 scenarios from verqo&apos;s internal 12-month model. Projections, not guarantees.</p>

          {/* Iter 242 · Financial forecast chart — the "exponential revenue,
              linear cost" story visualized. Pure SVG so it prints cleanly
              in PDF exports. */}
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="econ-forecast-chart">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <Eyebrow testid="forecast-eyebrow">3-year forecast</Eyebrow>
                <h4 className="mt-2 text-[18px] md:text-[22px] font-semibold tracking-tight text-white">
                  Revenue compounds. Cost stays flat.
                </h4>
              </div>
              <div className="flex items-center gap-4 text-[11px]">
                <div className="flex items-center gap-1.5"><span className="w-3 h-[3px] bg-lime-300 inline-block rounded"></span><span className="text-white/70">Revenue</span></div>
                <div className="flex items-center gap-1.5"><span className="w-3 h-[3px] bg-rose-400 inline-block rounded"></span><span className="text-white/70">Cost</span></div>
              </div>
            </div>
            <svg viewBox="0 0 600 220" className="mt-4 w-full h-auto" xmlns="http://www.w3.org/2000/svg" aria-label="Revenue and cost forecast">
              {/* Grid lines */}
              {[0, 55, 110, 165, 220].map((y, i) => (
                <line key={i} x1="40" y1={y} x2="600" y2={y} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
              ))}
              {/* Y-axis labels */}
              {[
                { y: 10,  l: "$50M" },
                { y: 65,  l: "$35M" },
                { y: 120, l: "$20M" },
                { y: 175, l: "$8M" },
                { y: 215, l: "$0" },
              ].map((t, i) => (
                <text key={i} x="0" y={t.y + 4} className="fill-white/40" style={{ fontSize: 10, fontFamily: "ui-monospace, monospace" }}>{t.l}</text>
              ))}
              {/* X-axis labels */}
              {[
                { x: 90,  l: "Y1" },
                { x: 220, l: "Y2" },
                { x: 350, l: "Y3" },
                { x: 480, l: "Y4" },
                { x: 570, l: "Y5" },
              ].map((t, i) => (
                <text key={i} x={t.x} y="212" className="fill-white/50" style={{ fontSize: 11, fontFamily: "ui-monospace, monospace" }}>{t.l}</text>
              ))}
              {/* Cost line — linear */}
              <line x1="60" y1="180" x2="580" y2="130" stroke="rgb(251, 113, 133)" strokeWidth="2.5" strokeDasharray="4 3" />
              {/* Revenue line — exponential curve */}
              <path
                d="M60,190 Q180,175 260,145 T430,60 T580,15"
                fill="none" stroke="rgb(190, 242, 100)" strokeWidth="3"
              />
              {/* Revenue area fill */}
              <path
                d="M60,190 Q180,175 260,145 T430,60 T580,15 L580,200 L60,200 Z"
                fill="rgba(190, 242, 100, 0.08)"
              />
              {/* Endpoints */}
              <circle cx="580" cy="15" r="4" fill="rgb(190, 242, 100)" />
              <circle cx="580" cy="130" r="4" fill="rgb(251, 113, 133)" />
              <text x="530" y="10" className="fill-lime-200" style={{ fontSize: 11, fontFamily: "ui-sans-serif" }}>$45M ARR</text>
              <text x="530" y="122" className="fill-rose-200" style={{ fontSize: 11, fontFamily: "ui-sans-serif" }}>$9M cost</text>
            </svg>
            <p className="mt-3 text-[11.5px] text-white/55 leading-relaxed">
              8 revenue streams on the same GMV (SaaS subs + processing + shipping margin + financing + insurance + banking) compound
              on shared infrastructure. Cost scales with ops headcount, not with volume. The gap widens every quarter.
            </p>
          </div>
        </div>

        <footer className="mt-14 pt-6 border-t border-white/5 flex items-center justify-between text-[10.5px] uppercase tracking-[0.25em] text-white/30">
          <span>verqo · the operating system for wholesale · wholesale, instant</span>
          <span>page 2</span>
        </footer>
      </section>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* PAGE 3 — Roadmap · Moat · Thesis                                   */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen px-8 md:px-20 py-12 md:py-16 max-w-[1280px] mx-auto border-t border-white/5" data-testid="page-3">
        {/* Page 3 header removed Iter 245 · sticky PitchIndex is the single deck header. */}

        {/* Competitive matrix */}
        <div className="mb-16 scroll-mt-14" data-testid="competitive-matrix" id="competition">
          <Eyebrow testid="matrix-eyebrow">How we stack up</Eyebrow>
          <h3 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight">
            Everyone else picks a lane. <span className="text-lime-300">We own the whole road.</span>
          </h3>
          <div className="mt-7 overflow-x-auto rounded-2xl border border-white/10">
            <table className="w-full min-w-[1040px] text-left border-collapse" data-testid="matrix-table">
              <thead>
                <tr className="bg-white/[0.03] border-b border-white/10">
                  <th className="p-3 text-[11px] uppercase tracking-[0.14em] text-white/45 font-medium">Capability</th>
                  <th className="p-3 text-center text-[12.5px] font-semibold text-lime-300 bg-lime-400/[0.06]">verqo</th>
                  <th className="p-3 text-center text-[12px] font-medium text-white/55">TreviPay</th>
                  <th className="p-3 text-center text-[12px] font-medium text-white/55">Resolve</th>
                  <th className="p-3 text-center text-[12px] font-medium text-white/55">Faire</th>
                  <th className="p-3 text-center text-[12px] font-medium text-white/55">JOOR</th>
                  <th className="p-3 text-center text-[12px] font-medium text-white/55">Shopify</th>
                  <th className="p-3 text-center text-[12px] font-medium text-white/55">Wholesail</th>
                  <th className="p-3 text-center text-[12px] font-medium text-white/55">Paystand</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { cap: "Serves buyers AND sellers natively", v: "y", t: "n", r: "n", f: "p", j: "p", s: "n", w: "p", ps: "p" },
                  { cap: "Owns the money rails (not a reseller)", v: "y", t: "n", r: "n", f: "p", j: "n", s: "p", w: "p", ps: "p" },
                  { cap: "Embedded credit insurance (Allianz Trade)", v: "y", t: "n", r: "n", f: "n", j: "n", s: "n", w: "n", ps: "n" },
                  { cap: "Multi-funder credit stack", v: "y", t: "p", r: "n", f: "n", j: "n", s: "p", w: "p", ps: "n" },
                  { cap: "Portable AI Buyer Score across network", v: "y", t: "p", r: "p", f: "p", j: "n", s: "n", w: "p", ps: "n" },
                  { cap: "Invoice factoring for sellers", v: "y", t: "n", r: "n", f: "n", j: "n", s: "n", w: "n", ps: "n" },
                  { cap: "Embedded shipping & freight", v: "y", t: "n", r: "n", f: "p", j: "n", s: "p", w: "n", ps: "n" },
                  { cap: "Built-in dropship network (supplier ↔ retailer)", v: "y", t: "n", r: "n", f: "p", j: "n", s: "p", w: "n", ps: "n" },
                  { cap: "AI catalog import + open Catalog API", v: "y", t: "n", r: "n", f: "p", j: "p", s: "p", w: "n", ps: "n" },
                  { cap: "Bank-grade compliance suite (AML · OFAC · SR 11-7)", v: "y", t: "p", r: "p", f: "n", j: "n", s: "p", w: "n", ps: "p" },
                  { cap: "Single unified ledger for every party", v: "y", t: "n", r: "n", f: "p", j: "p", s: "p", w: "p", ps: "p" },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-white/5 last:border-0" data-testid={`matrix-row-${i}`}>
                    <td className="p-3 text-[12.5px] text-white/75">{row.cap}</td>
                    <td className="p-3 text-center bg-lime-400/[0.05]"><MCell state={row.v} /></td>
                    <td className="p-3 text-center"><MCell state={row.t} /></td>
                    <td className="p-3 text-center"><MCell state={row.r} /></td>
                    <td className="p-3 text-center"><MCell state={row.f} /></td>
                    <td className="p-3 text-center"><MCell state={row.j} /></td>
                    <td className="p-3 text-center"><MCell state={row.s} /></td>
                    <td className="p-3 text-center"><MCell state={row.w} /></td>
                    <td className="p-3 text-center"><MCell state={row.ps} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-5 text-[11px] text-white/45" data-testid="matrix-legend">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-lime-300" /> Native</span>
            <span className="flex items-center gap-1.5"><CircleDot className="w-3 h-3 text-amber-400" /> Partial / via partner</span>
            <span className="flex items-center gap-1.5"><span className="text-white/30">—</span> Not offered</span>
          </div>
        </div>

        {/* The 2-4 year roadmap */}
        <div className="mt-14 scroll-mt-14" id="roadmap">
          <Eyebrow testid="future-eyebrow">The next 2-4 years</Eyebrow>
          <h3 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight">
            From US wholesale → <span className="text-lime-300">global B2B infrastructure.</span>
          </h3>
          <div className="mt-7 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="roadmap">
            <div className="rounded-xl border border-white/10 p-5" data-testid="year-1">
              <div className="text-lime-300 text-[10.5px] uppercase tracking-[0.18em] mb-2">2026 · Year 1</div>
              <div className="text-sm font-semibold mb-2 text-white">Crack US wholesale</div>
              <p className="text-[12px] text-white/60 leading-relaxed">~$8T addressable. Sponsor-bank rails live. Allianz integration. Buyer + seller funnels at scale.</p>
            </div>
            <div className="rounded-xl border border-white/10 p-5" data-testid="year-2">
              <div className="text-lime-300 text-[10.5px] uppercase tracking-[0.18em] mb-2">2027 · Year 2</div>
              <div className="text-sm font-semibold mb-2 text-white">EU + UK rollout</div>
              <p className="text-[12px] text-white/60 leading-relaxed">Allianz Trade already pan-European. SEPA + Faster-Payments rails. Localized KYB.</p>
            </div>
            <div className="rounded-xl border border-white/10 p-5" data-testid="year-3">
              <div className="text-lime-300 text-[10.5px] uppercase tracking-[0.18em] mb-2">2028 · Year 3</div>
              <div className="text-sm font-semibold mb-2 text-white">LATAM + APAC</div>
              <p className="text-[12px] text-white/60 leading-relaxed">The biggest underserved opportunity. Cross-border FX. Local-rail partnerships. Trade-finance product layer.</p>
            </div>
            <div className="rounded-xl border border-white/10 p-5 bg-lime-400/[0.04]" data-testid="year-4">
              <div className="text-lime-300 text-[10.5px] uppercase tracking-[0.18em] mb-2">2029-30 · Year 4+</div>
              <div className="text-sm font-semibold mb-2 text-white">Embedded wholesale credit</div>
              <p className="text-[12px] text-white/60 leading-relaxed">White-label our infrastructure. Banks build on top of verqo. Same play as Stripe / Plaid did for their categories.</p>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="adjacent">
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/45 mb-3">Adjacent products we can ship</div>
              <ul className="space-y-1.5 text-[12.5px] text-white/70">
                <li>· Payroll-on-demand for SMB sellers (we already see their cashflow)</li>
                <li>· Merchant-cash-advance against settled receivables</li>
                <li>· Cross-border FX on trade-finance corridors</li>
                <li>· Supply-chain financing for tier-2 / tier-3 suppliers</li>
                <li>· Verqo-native marketplace (sellers list, buyers source)</li>
              </ul>
            </div>
            <div className="rounded-xl border border-lime-400/30 bg-lime-400/[0.04] p-5" data-testid="moat">
              <div className="text-[11px] uppercase tracking-[0.18em] text-lime-300 mb-3">Why we win · the compounding moat</div>
              <ul className="space-y-1.5 text-[12.5px] text-white/80">
                <li>· <strong className="text-white">Two-sided network effects</strong> — every new seller brings ~30 of their buyers</li>
                <li>· <strong className="text-white">Bank-grade compliance moat</strong> — 37+ examiner-grade dashboards live</li>
                <li>· <strong className="text-white">AI-native decisioning</strong> — improves with every transaction</li>
                <li>· <strong className="text-white">Insurance-backed</strong> — Allianz Trade integration baked in</li>
                <li>· <strong className="text-white">Multi-funder credit stack</strong> — lender, seller, platform, insurer</li>
                <li>· <strong className="text-white">Owned rails</strong> — not a thin reseller wrapper</li>
              </ul>
            </div>
          </div>
        </div>

        {/* ═══ Iter 242 · The Ask ═══════════════════════════════════════
            Framed as "capital deployment" rather than a traditional VC raise,
            reflecting Verqo's $10M family-backed bootstrap posture from the
            diligence page. If a strategic investor / debt facility is on the
            table, this is where they see the allocation math. */}
        <div className="mt-16 pt-10 border-t border-white/5 scroll-mt-14" data-testid="ask-slide" id="ask">
          <Eyebrow testid="ask-eyebrow">The ask</Eyebrow>
          <h2 className="mt-3 text-[32px] md:text-[44px] leading-[0.98] font-semibold tracking-tight">
            <span className="text-lime-300">$5M</span> to hit $1B in bank-cleared volume.
          </h2>
          <p className="mt-4 max-w-3xl text-[14px] md:text-[15px] leading-relaxed text-white/65">
            Verqo is 100% bootstrapped today with a $10M family-fund commitment on standby.
            The next $5M tranche unlocks the operating team and the bank-reserve capacity
            required to clear $1B in transaction volume in 12 months.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ask-buckets">
            <div className="rounded-2xl border border-lime-400/25 bg-lime-400/[0.05] p-5">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-lime-300/80">Growth capital</div>
              <div className="mt-2 text-[36px] md:text-[44px] font-semibold tracking-tight text-white tabular-nums leading-none">$2M</div>
              <div className="mt-2 text-[13px] font-medium text-white/85">Opex · Hiring · GTM</div>
              <ul className="mt-3 space-y-1.5 text-[12px] leading-relaxed text-white/60">
                <li>· 3 GTM hires (partnerships, sales, CS)</li>
                <li>· 4 engineering hires (risk, underwriting, API)</li>
                <li>· BSA officer + compliance lead</li>
                <li>· Direct + partnership acquisition engine</li>
                <li>· 18-month runway to $1B GMV milestone</li>
              </ul>
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/[0.03] p-5">
              <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-white/60">Bank reserve</div>
              <div className="mt-2 text-[36px] md:text-[44px] font-semibold tracking-tight text-white tabular-nums leading-none">$3M</div>
              <div className="mt-2 text-[13px] font-medium text-white/85">Sponsor-bank collateral</div>
              <ul className="mt-3 space-y-1.5 text-[12px] leading-relaxed text-white/60">
                <li>· Collateralized against U.S. Bank integration</li>
                <li>· Backstops ACH/RTP settlement float</li>
                <li>· Enables Day-0 seller payouts at scale</li>
                <li>· Regulatory capital for growth beyond $1B/yr</li>
                <li>· Interest-bearing while idle</li>
              </ul>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="ask-milestones">
            {[
              { l: "6 months", v: "175 sellers · 3,500 buyers", s: "onboarded + activated" },
              { l: "12 months", v: "$1B bank-cleared", s: "settled through U.S. Bank" },
              { l: "24 months", v: "Series A on our terms", s: "or continued bootstrap · optional" },
            ].map(m => (
              <div key={m.l} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-white/50">{m.l}</div>
                <div className="mt-1.5 text-[16px] font-semibold text-white">{m.v}</div>
                <div className="mt-0.5 text-[11.5px] text-white/60">{m.s}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ═══ Iter 242 · Path to Exit ═══════════════════════════════════════
            Investor-friendly framing of the return story — comps + strategic
            acquirers + horizon. Real numbers, not vibes. */}
        <div className="mt-16 pt-10 border-t border-white/5 scroll-mt-14" data-testid="exit-slide" id="exit">
          <Eyebrow testid="exit-eyebrow">Path to exit</Eyebrow>
          <h2 className="mt-3 text-[32px] md:text-[44px] leading-[0.98] font-semibold tracking-tight">
            A <span className="text-lime-300">5-year path</span> to a strategic exit — with a public option.
          </h2>
          <p className="mt-4 max-w-3xl text-[14px] md:text-[15px] leading-relaxed text-white/65">
            Wholesale-tech has more strategic acquirers than most vertical categories, and
            the multiples reflect the recurring-revenue + payment-take-rate + embedded-lending
            triple stack.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="exit-comps">
            {[
              { l: "Comps · SaaS", v: "8-12×", s: "revenue · public SaaS + fintech" },
              { l: "Comps · payments", v: "20-30×", s: "adj. EBITDA · Stripe, Adyen, Toast" },
              { l: "Comps · embedded finance", v: "$8-15B", s: "outcomes · Marqeta, Bill.com" },
            ].map(c => (
              <div key={c.l} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-white/50">{c.l}</div>
                <div className="mt-2 text-[28px] font-semibold tracking-tight text-lime-300 tabular-nums leading-none">{c.v}</div>
                <div className="mt-2 text-[11.5px] text-white/60">{c.s}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="exit-acquirers">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-white/50">Strategic acquirers watching this space</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {[
                "Shopify · B2B ambitions",
                "Stripe · own the money movement",
                "Amazon Business · wholesale marketplace",
                "SAP Ariba · enterprise procurement",
                "Flexport · logistics + finance",
                "Bill.com · SMB accounting hub",
                "McKesson · vertical wholesale plays",
                "TreviPay · consolidation buyer",
              ].map(a => (
                <span key={a} className="text-[11px] mono uppercase tracking-[0.14em] px-2 py-1 rounded border border-white/10 text-white/70">
                  {a}
                </span>
              ))}
            </div>
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { l: "3-year target", v: "$100M ARR", s: "Series B optionality · $1-2B valuation" },
              { l: "5-year target", v: "$500M ARR", s: "Strategic sale · $4-6B enterprise value" },
              { l: "7-year target", v: "$1B+ ARR", s: "IPO track · public wholesale-tech benchmark" },
            ].map(t => (
              <div key={t.l} className="rounded-xl border border-lime-400/20 bg-lime-400/[0.03] p-4">
                <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-lime-300/80">{t.l}</div>
                <div className="mt-1.5 text-[18px] font-semibold text-white">{t.v}</div>
                <div className="mt-0.5 text-[11.5px] text-white/60">{t.s}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Closing */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-end pb-8" data-testid="closing">
          <div>
            <Eyebrow>The thesis</Eyebrow>
            <h2 className="mt-4 text-[36px] md:text-[52px] leading-[0.98] font-semibold tracking-tight">
              Wholesale is the <span className="text-white/40">last</span> giant industry without<br />
              its own <span className="text-lime-300">native operating system.</span>
            </h2>
            <p className="mt-5 max-w-2xl text-[14px] leading-relaxed text-white/65">
              Stripe owned online payments. Shopify owned online retail. Plaid owned bank-account access.
              <strong className="text-white"> Verqo will own global B2B wholesale</strong> — because we're the first to fix the two-sided
              liquidity, compliance, and trust problem with one unified stack.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2 text-lime-300">
              <Network className="w-4 h-4" />
              <span className="text-[11px] uppercase tracking-[0.18em]">Built for #1 globally</span>
            </div>
            <a href="/register?as=seller" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-lime-300 text-[#0a0e0a] text-sm font-semibold hover:bg-lime-200 transition-colors" data-testid="cta-link">
              See it live
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        <footer className="mt-6 pt-6 border-t border-white/5 flex items-center justify-between text-[10.5px] uppercase tracking-[0.25em] text-white/30">
          <span>verqo · the operating system for wholesale · wholesale, instant</span>
          <span>page 3</span>
        </footer>
      </section>
    </div>
  );
}
